/**
 * @sazeo/sdk — MFA settings hooks
 *
 * Reads/writes the `user_mfa_settings` table (tenant-scoped audit/enforcement layer).
 * Actual TOTP factor management (enroll/verify/unenroll/list) lives in security.ts
 * and uses Supabase Auth's auth.mfa.* API directly.
 *
 * All calls use the user JWT. No service_role.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserMfaSettings {
  id: string;
  tenant_id: string;
  user_id: string;
  mfa_required: boolean;
  enrolled_at: string | null;
  last_verified_at: string | null;
  created_at: string;
}

export interface UpdateMfaSettingsInput {
  mfa_required?: boolean;
  enrolled_at?: string | null;
  last_verified_at?: string | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const mfaSettingsKeys = {
  all: ['mfa-settings'] as const,
  me: (tenantId: string) => [...mfaSettingsKeys.all, tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// useMfaSettings — reads the current user's user_mfa_settings row
// ---------------------------------------------------------------------------

export function useMfaSettings(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: mfaSettingsKeys.me(tenantId),
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = (sessionData.session as { user?: { id?: string } } | null)?.user?.id;
      if (!userId) return null;

      const { data, error } = await supabase
        .from('user_mfa_settings')
        .select('id, tenant_id, user_id, mfa_required, enrolled_at, last_verified_at, created_at')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .maybeSingle();

      if (error)
        throw new Error((error as { message?: string }).message ?? 'Failed to load MFA settings');
      return data as UserMfaSettings | null;
    },
    enabled: Boolean(tenantId),
  });
}

// ---------------------------------------------------------------------------
// useUpdateMfaSettings — upserts the current user's user_mfa_settings row
// ---------------------------------------------------------------------------

export function useUpdateMfaSettings(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMfaSettingsInput) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = (sessionData.session as { user?: { id?: string } } | null)?.user?.id;
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_mfa_settings')
        .upsert(
          { tenant_id: tenantId, user_id: userId, ...input },
          { onConflict: 'tenant_id,user_id' },
        )
        .select('id, tenant_id, user_id, mfa_required, enrolled_at, last_verified_at, created_at')
        .single();

      if (error)
        throw new Error((error as { message?: string }).message ?? 'Failed to update MFA settings');
      return data as UserMfaSettings;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mfaSettingsKeys.me(tenantId) });
    },
  });
}
